import React, { useState, useEffect, useRef } from 'react';
import styles from './CodeAnimation.module.css';

// Tweak these two constants to control the overall speed
const STEP_INTERVAL = 180; // ms between each line appearing (50% slower)
const PROGRESS_TICK = 25;  // ms between progress bar increments

const bootSequence = [
  { type: 'title',   text: 'KILONOVA OS v2.5.1 - INITIATING...', delay: 200 },
  { type: 'progress'                                              },
  { type: 'section', text: '> Loading modules...',                delay: 150 },
  { type: 'check',   text: '  ✓ React Engine initialized',        delay: 165 },
  { type: 'check',   text: '  ✓ Node.js runtime ready',           delay: 190 },
  { type: 'check',   text: '  ✓ Database connections established', delay: 420 }, // slow patch - working harder
  { type: 'check',   text: '  ✓ Cloud services online',           delay: 380 }, // slow patch continues
  { type: 'section', text: '> Compiling business logic...',       delay: 140 },
  { type: 'check',   text: '  ✓ Authentication system',           delay: 95  }, // stutter - quick catchup
  { type: 'check',   text: '  ✓ Payment processing',              delay: 110 }, // stutter recovery
  { type: 'check',   text: '  ✓ Data analytics',                  delay: 195 },
  { type: 'section', text: '> Deploying solution...',             delay: 350 }, // brief hesitation
  { type: 'check',   text: '  ✓ AWS infrastructure provisioned',  delay: 85  }, // quick catchup
  { type: 'check',   text: '  ✓ SSL certificates installed',      delay: 175 },
  { type: 'final',   text: 'SYSTEM READY. Building your console...', delay: 220 },
];

const CodeAnimation = ({ onComplete }) => {
  const [visibleSteps, setVisibleSteps] = useState(0);
  const [progress, setProgress]         = useState(0);
  const onCompleteRef = useRef(onComplete);

  useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);

  // Step sequencer with per-line delay overrides
  useEffect(() => {
    if (visibleSteps >= bootSequence.length) {
      const done = setTimeout(() => onCompleteRef.current?.(), 400);
      return () => clearTimeout(done);
    }
    const currentStep = bootSequence[visibleSteps];
    const delay = currentStep?.delay ?? STEP_INTERVAL;
    const timer = setTimeout(
      () => setVisibleSteps(prev => prev + 1),
      delay
    );
    return () => clearTimeout(timer);
  }, [visibleSteps]);

  // Progress bar tick — runs continuously after progress bar appears
  useEffect(() => {
    const progressStepIndex = bootSequence.findIndex(s => s.type === 'progress');
    if (visibleSteps <= progressStepIndex || progress >= 100) return;

    // Calculate target progress based on animation completion
    const totalSteps = bootSequence.length - progressStepIndex - 1;
    const currentProgress = visibleSteps - progressStepIndex - 1;
    const targetProgress = Math.min((currentProgress / totalSteps) * 100, 100);

    // Gradually increment toward target
    const tick = setTimeout(
      () => setProgress(prev => {
        if (prev >= targetProgress) return prev;
        const increment = Math.max(1, Math.ceil((targetProgress - prev) / 8));
        return Math.min(prev + increment, 100);
      }),
      PROGRESS_TICK
    );
    return () => clearTimeout(tick);
  }, [visibleSteps, progress]);

  const renderProgressBar = () => {
    const filled = Math.floor(progress / 5);
    return `[${'█'.repeat(filled)}${' '.repeat(20 - filled)}] ${progress}%`;
  };

  return (
    <div className={styles.codeAnimationOverlay}>
      <div className={styles.terminal}>
        <div className={styles.terminalHeader}>
          <div className={styles.terminalButtons}>
            <span className={`${styles.terminalButton} ${styles.btnClose}`}    />
            <span className={`${styles.terminalButton} ${styles.btnMinimise}`} />
            <span className={`${styles.terminalButton} ${styles.btnExpand}`}   />
          </div>
          <div className={styles.terminalTitle}>kilonova-boot-sequence.sh</div>
        </div>

        <div className={styles.terminalBody}>
          <div className={styles.bootSequence}>
            {bootSequence.slice(0, visibleSteps).map((step, index) =>
              step.type === 'progress' ? (
                <div key={index} className={styles.progressLine}>
                  {renderProgressBar()}
                </div>
              ) : (
                <div key={index} className={`${styles.bootLine} ${styles[step.type]}`}>
                  {step.text}
                </div>
              )
            )}
            {visibleSteps < bootSequence.length && (
              <span className={styles.cursor}>_</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CodeAnimation;